import { DeleteAccountTypeEnum } from 'api/enums';

interface IPrivacyPolicyProps {
    type?: DeleteAccountTypeEnum;
}

const PrivacyPolicy = (props: IPrivacyPolicyProps) => {
    const { type } = props;

    return (
        <div className="min-h-screen  py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="px-8 py-10 sm:px-12 sm:py-12">
                    <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
                        <p className="text-gray-700 leading-relaxed">
                            {type === DeleteAccountTypeEnum.PROGRESS_CENTER ? 'ProgressCenter' : 'SiteWalk Capture'}{' '}
                            ("we", "us", "our") operates the website{' '}
                            <a
                                href="https://v4.progresscenter.io"
                                className="text-blue-600 hover:text-blue-800 underline"
                            >
                                https://v4.progresscenter.io
                            </a>{' '}
                            (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard
                            your information when you visit or use the Service. By accessing or using the Service you
                            accept this Policy.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>

                        <div className="space-y-4">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">a) Personal Data</h3>
                                <p className="text-gray-700 leading-relaxed">
                                    We may collect personal information that you provide voluntarily when you register
                                    for an account, such as your name, email address, company name, job title, telephone
                                    number, and credentials.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">b) Usage Data</h3>
                                <p className="text-gray-700 leading-relaxed">
                                    When you use the Service, we may automatically collect information about how the
                                    Service is accessed and used ("Usage Data"). This may include your IP address,
                                    browser type and version, pages of our Service that you visit, time and date of
                                    access, device identifiers, and other diagnostic data.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">c) Images & Media</h3>
                                <p className="text-gray-700 leading-relaxed">
                                    As part of the Project Monitoring services discussed on our site, you may upload or
                                    we may process images, video, or other media files of your project sites.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">d) Cookies & Tracking</h3>
                                <p className="text-gray-700 leading-relaxed">
                                    We use cookies and similar tracking technologies to track activity on our Service
                                    and hold certain information.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Use of Your Information</h2>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            We use the information we collect for purposes including:
                        </p>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>to provide, operate and maintain the Service;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>to improve, personalize and expand our Service;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>to manage your account and user access;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>to process transactions and send you related information;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>to monitor and analyze usage and trends;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>to detect, prevent and address technical issues;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>to communicate updates, offers or marketing material (where permitted);</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>to comply with legal obligations.</span>
                            </li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Sharing Your Information</h2>
                        <p className="text-gray-700 leading-relaxed mb-3">We may share your information:</p>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2 ">•</span>
                                <span>
                                    With service providers and partners who perform services on our behalf (hosting, IT,
                                    analytics);
                                </span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2 ">•</span>
                                <span>
                                    To comply with legal obligations, respond to lawful requests, protect and defend our
                                    rights;
                                </span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2 ">•</span>
                                <span>
                                    In connection with business transfers, mergers or acquisitions (with notice).
                                </span>
                            </li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
                        <p className="text-gray-700 leading-relaxed">
                            We implement reasonable administrative, technical, and physical security measures to help
                            protect your personal information. However, no method of transmission over the Internet or
                            electronic storage is completely secure, and we cannot guarantee absolute security.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights</h2>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            Depending on your jurisdiction you may have the right to:
                        </p>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Access and request copies of your personal data;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Object to or restrict processing of your data;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Update or correct inaccurate data;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Request deletion of your data (subject to legal retention obligations);</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Withdraw consent where we rely on it;</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>Receive your data in a structured, machine-readable format.</span>
                            </li>
                        </ul>
                        <p className="text-gray-700 leading-relaxed mt-4">
                            If you are located in the EU or UK, you also have rights under GDPR. If you are a California
                            resident, you have rights under the CCPA/CPRA.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cookies and Tracking Technologies</h2>
                        <p className="text-gray-700 leading-relaxed">
                            We use cookies and similar technologies for website functionality, analytics, and
                            advertising. You can instruct your browser to refuse cookies or alert you when cookies are
                            being sent. Note that if you disable cookies, some parts of the Service may not function
                            properly.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">International Transfers</h2>
                        <p className="text-gray-700 leading-relaxed">
                            Your information, including personal data, may be transferred to — and maintained on —
                            computers located outside your state, province, country or other governmental jurisdiction
                            where data protection laws may differ. By using the Service you consent to such transfers.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Retention of Data</h2>
                        <p className="text-gray-700 leading-relaxed">
                            We will retain your personal information only for as long as is necessary for the purposes
                            set out in this Policy unless a longer retention period is required or permitted by law.
                            Usage Data may be retained for analytics.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
                        <p className="text-gray-700 leading-relaxed">
                            We may update this Privacy Policy from time to time. If we make changes, we will post the
                            new Policy on this page and update the "Effective Date".
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
                        <p className="text-gray-700 leading-relaxed">
                            If you have any questions about this Privacy Policy, please contact us at:
                        </p>
                        <p className="text-gray-700 leading-relaxed mt-3">
                            <span className="font-medium">Email:</span>{' '}
                            <a
                                href="mailto:info@timelapseme.com"
                                className="text-blue-600 hover:text-blue-800 underline"
                            >
                                info@timelapseme.com
                            </a>
                        </p>
                    </section>

                    <div className="mt-12 pt-8 border-t border-gray-200">
                        <p className="text-sm text-gray-500 text-center">
                            © {new Date().getFullYear()}{' '}
                            {type === DeleteAccountTypeEnum.PROGRESS_CENTER ? 'ProgressCenter' : 'SiteWalk Capture'}.
                            All rights reserved.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default PrivacyPolicy;
